import { useState, useEffect } from "react";
import { Trans, t } from "@lingui/macro";
import { X, Camera, Loader2, Moon, Sun } from "lucide-react";
import { defaultLanguage } from "../../locales/InitTranslation";
import { Fire } from "../../services/FireClient";
import { My } from "../../services/My";
import { Keys } from "../main/AccountLanguage";
import package_json from "../../../package.json";
import { Capacitor } from "@capacitor/core";
import { otaService } from "../../services/Utils/OtaUpdateService";

import { Dialog, DialogContent } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { useToast } from "../../components/ui/use-toast";
import { Switch } from "../../components/ui/switch";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import SelectProfileImageModal from "./SelectProfileImageModal";
import { UserAvatar } from "./UserAvatar";
import { getThemePreference, setThemePreference, type ThemeMode } from "./ThemeManager";

interface MyProfileModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export const MyProfileModal: React.FC<MyProfileModalProps> = ({ open, onOpenChange }) => {
    const [profileImage, setProfileImage] = useState(My.user.photoUrl || "");
    const [name, setName] = useState(My.user.name);
    const [description, setDescription] = useState(My.user.description || "");
    const [language, setLanguage] = useState(My.user.lang ?? defaultLanguage);
    const [originalLanguage] = useState(My.user.lang ?? defaultLanguage);
    const isLanguageChanged = language !== originalLanguage;
    const [inProgress, setInProgress] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showFinalDeleteConfirm, setShowFinalDeleteConfirm] = useState(false);
    const { toast } = useToast();
    const [themeMode, setThemeMode] = useState<ThemeMode>(getThemePreference);

    useEffect(() => {
        if (open) {
            setProfileImage(My.user.photoUrl || "");
            setName(My.user.name);
            setDescription(My.user.description || "");
            setLanguage(My.user.lang ?? defaultLanguage);
        }
    }, [open]);

    async function profileImgChanged(url: string) {
        setProfileImage(url);
        My.user.photoUrl = url;
        await Fire.saveUser(My.user);
        toast({ title: t`Profile image saved`, duration: 2000 });
    }

    async function saveProfile() {
        setInProgress(true);
        try {
            const langChanged = My.user.lang !== language;
            My.user.lang = language;
            My.user.name = name;
            My.user.description = description;
            await Fire.saveUser(My.user);

            if (langChanged) {
                window.location.reload();
            } else {
                toast({ title: t`Profile updated`, duration: 2000 });
                onOpenChange(false);
            }
        } catch (e) {
            toast({ title: t`Failed to update profile`, duration: 2000 });
        } finally {
            setInProgress(false);
        }
    }

    async function deleteAccount() {
        setInProgress(true);
        try {
            await Fire.auth.deleteAccount();
            window.location.href = '/';
        } catch (e) {
            toast({ title: t`Failed to delete account`, duration: 2000 });
            setInProgress(false);
        }
    }

    return (
        <>
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="max-w-lg w-[95vw] max-h-[85vh] flex flex-col p-0 gap-0 overflow-hidden">
                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b">
                        <h2 className="text-lg font-semibold"><Trans>My Profile</Trans></h2>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto p-6 space-y-6">
                        {/* Profile Image */}
                        <div className="flex flex-col items-center gap-4">
                            <div className="relative">
                                <UserAvatar
                                    src={profileImage}
                                    name={name}
                                    className="w-32 h-32"
                                />
                                <SelectProfileImageModal
                                    loginImage={profileImage}
                                    name={name}
                                    onSelected={profileImgChanged}
                                />
                            </div>
                            <p className="text-sm text-muted-foreground text-center">
                                <Trans>Choose an avatar or upload a new profile picture.</Trans>
                            </p>
                        </div>

                        {/* Email */}
                        {My.user.email && (
                            <div className="space-y-2">
                                <Label htmlFor="profile-email"><Trans>Email</Trans></Label>
                                <Input
                                    id="profile-email"
                                    value={My.user.email}
                                    disabled
                                    className="bg-muted/50 text-foreground"
                                />
                            </div>
                        )}

                        {/* Name */}
                        <div className="space-y-2">
                            <Label htmlFor="profile-name"><Trans>Name</Trans></Label>
                            <Input
                                id="profile-name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                maxLength={30}
                                data-cy="name-input"
                            />
                        </div>

                        {/* About Me */}
                        <div className="space-y-2">
                            <Label htmlFor="profile-description"><Trans>About Me</Trans></Label>
                            <Textarea
                                id="profile-description"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                rows={3}
                            />
                        </div>

                        {/* Language */}
                        <div className="space-y-2">
                            <Label><Trans>Language</Trans></Label>
                            <Select value={language} onValueChange={setLanguage}>
                                <SelectTrigger>
                                    <SelectValue placeholder={t`Select language`} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value={Keys.EN}>English</SelectItem>
                                    <SelectItem value={Keys.KO}>한국어</SelectItem>
                                    <SelectItem value={Keys.JA}>日本語</SelectItem>
                                </SelectContent>
                            </Select>
                            {isLanguageChanged && (
                                <p className="text-sm text-primary bg-primary/10 px-3 py-2 rounded-md border border-primary/20">
                                    <Trans>The page will refresh to apply the new language when you save.</Trans>
                                </p>
                            )}
                        </div>

                        {/* Dark Mode */}
                        <div className="space-y-2">
                            <Label><Trans>Dark Mode</Trans></Label>
                            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                                <div className="flex items-center gap-3">
                                    {themeMode === 'auto' ? <Moon className="w-4 h-4 text-muted-foreground" /> : <Sun className="w-4 h-4 text-muted-foreground" />}
                                    <div>
                                        <p className="text-sm font-medium">
                                            {themeMode === 'auto' ? <Trans>Auto (Follow system)</Trans> : <Trans>Always Light</Trans>}
                                        </p>
                                        <p className="text-xs text-muted-foreground">
                                            {themeMode === 'auto'
                                                ? <Trans>Dark mode activates based on your device settings.</Trans>
                                                : <Trans>Always use light mode regardless of device settings.</Trans>
                                            }
                                        </p>
                                    </div>
                                </div>
                                <Switch
                                    checked={themeMode === 'auto'}
                                    onCheckedChange={(checked) => {
                                        const newMode: ThemeMode = checked ? 'auto' : 'light';
                                        setThemeMode(newMode);
                                        setThemePreference(newMode);
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between px-6 py-4 border-t bg-muted/50">
                        <Button
                            variant="ghost"
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={() => setShowDeleteConfirm(true)}
                        >
                            <Trans>Delete Account</Trans>
                        </Button>
                        <Button onClick={saveProfile} disabled={inProgress || !name.trim()} data-cy="save-btn">
                            {inProgress ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                            <Trans>Save changes</Trans>
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation */}
            <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle><Trans>Delete Account</Trans></AlertDialogTitle>
                        <AlertDialogDescription>
                            <Trans>Do you want to delete your user account?</Trans>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel><Trans>Cancel</Trans></AlertDialogCancel>
                        <AlertDialogAction
                            className="bg-destructive hover:bg-destructive/90"
                            onClick={() => {
                                setShowDeleteConfirm(false);
                                setTimeout(() => setShowFinalDeleteConfirm(true), 300);
                            }}
                        >
                            <Trans>Yes, delete</Trans>
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Final Delete Confirmation */}
            <AlertDialog open={showFinalDeleteConfirm} onOpenChange={setShowFinalDeleteConfirm}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle><Trans>Final Confirmation</Trans></AlertDialogTitle>
                        <AlertDialogDescription>
                            <Trans>Do you really want to delete your user account? This action cannot be undone.</Trans>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel><Trans>Cancel</Trans></AlertDialogCancel>
                        <AlertDialogAction
                            className="bg-destructive hover:bg-destructive/90"
                            onClick={deleteAccount}
                            disabled={inProgress}
                        >
                            {inProgress ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                            <Trans>Yes, delete permanently</Trans>
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};

export default MyProfileModal;
